import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch, faTrash, faSpinner, faSave } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../ModalConversion';
import ConfirmationModal from '../../ConfirmationModal';
import AsyncSelect from 'react-select/async';
import AccountsUsersService from '../../../services/AccountsUsersService';
import { BUSINESS_MODELS, CONTENT_TYPES, APPLY_OPTIONS } from '../constants';
import { theme } from '../../../theme';

const darkStyles = {
    control: (provided, state) => ({
        ...provided,
        backgroundColor: theme.surface2,
        borderColor: state.isFocused ? theme.primary : theme.border,
        color: theme.text,
        '&:hover': { borderColor: state.isFocused ? theme.primary : theme.borderStrong },
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? theme.bg : theme.surface2,
        color: theme.text,
        cursor: 'pointer',
        '&:active': { backgroundColor: theme.border },
    }),
    multiValue: (provided) => ({ ...provided, backgroundColor: theme.bg }),
    multiValueLabel: (provided) => ({ ...provided, color: theme.text }),
    multiValueRemove: (provided) => ({ ...provided, color: theme.text, '&:hover': { backgroundColor: theme.dangerText, color: theme.text } }),
    singleValue: (provided) => ({ ...provided, color: theme.text }),
    input: (provided) => ({ ...provided, color: theme.text }),
    placeholder: (provided) => ({ ...provided, color: theme.textDisabled }),
    menu: (provided) => ({ ...provided, backgroundColor: theme.surface2, border: `1px solid ${theme.border}` }),
    menuList: (provided) => ({ ...provided, maxHeight: 220 }),
};

function PublisherSelect({ isMulti, value, onChange, placeholder }) {
    const loadOptions = useCallback(async (input, callback) => {
        try {
            const results = await AccountsUsersService.searchAccounts({ search: input || '', limit: 20 });
            callback(results.map((r) => ({
                label: r.label,
                value: r.id,
                pub_name: r.pub_name,
                prefix: r.prefix,
            })));
        } catch (e) {
            callback([]);
        }
    }, []);

    return (
        <AsyncSelect
            isMulti={isMulti}
            isClearable
            cacheOptions={false}
            defaultOptions
            loadOptions={loadOptions}
            value={value || (isMulti ? [] : null)}
            onChange={onChange}
            placeholder={placeholder}
            styles={darkStyles}
            theme={(themeObj) => ({ ...themeObj, colors: { ...themeObj.colors, primary: theme.primary } })}
        />
    );
}

/**
 * Porcentajes Ink It / Cuentas (migrated from admin/percent_publishers).
 * Two tabs: "Agregar" (new percentages) and "Buscar" (search rows of one account).
 */
export function PercentPublishersModal({ isOpen, onClose, initialTab = 'add', account = null }) {
    const [tab, setTab] = useState(initialTab);
    const [channels, setChannels] = useState([]);
    const [error, setError] = useState('');
    const [feedback, setFeedback] = useState('');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    // Add form
    const [idChannel, setIdChannel] = useState('0');
    const [contentType, setContentType] = useState('ebook');
    const [businessModel, setBusinessModel] = useState('sale');
    const [apply, setApply] = useState('pvp');
    const [percentInk, setPercentInk] = useState('15');
    const [percentPublisher, setPercentPublisher] = useState('-1');
    const [listChannels, setListChannels] = useState([]);
    const [publishers, setPublishers] = useState([]);

    // Search
    const [searchPub, setSearchPub] = useState(null);
    const [searchChannel, setSearchChannel] = useState('0');
    const [searchModel, setSearchModel] = useState('all');
    const [searchType, setSearchType] = useState('all');
    const [searchApply, setSearchApply] = useState('all');
    const [results, setResults] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setTab(initialTab);
        setError('');
        setFeedback('');
        setListChannels([]);
        setResults(null);
        setPublishers(
            account && account.id_pub
                ? [{ label: `[${account.prefix || ''}] ${account.pub_name}`, value: account.id_pub, pub_name: account.pub_name, prefix: account.prefix }]
                : []
        );
        AccountsUsersService.getChannels()
            .then((data) => setChannels(data || []))
            .catch(() => setChannels([]));
    }, [isOpen, initialTab, account]);

    const channelName = (cid) => {
        const ch = channels.find((c) => c.id === cid);
        return ch ? ch.name : `Canal ${cid}`;
    };

    const addToList = () => {
        setError('');
        setFeedback('');
        const candidates = idChannel === '0'
            ? channels.map((c) => c.id)
            : [Number(idChannel)];
        if (candidates.length === 0) {
            setError('Selecciona una tienda');
            return;
        }
        for (const cid of candidates) {
            if (listChannels.some((l) => l.id_channel === cid && l.business_model === businessModel && l.content_type === contentType && l.apply === apply)) {
                setError('Ya existen los datos. Verifique!');
                return;
            }
        }
        const next = [...listChannels];
        candidates.forEach((cid) => {
            next.push({
                id_channel: cid,
                channel_name: channelName(cid),
                percent_ink: percentInk,
                percent_publisher: percentPublisher,
                business_model: businessModel,
                content_type: contentType,
                apply,
            });
        });
        setListChannels(next);
    };

    const removeFromList = (index) => {
        setListChannels((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setError('');
        setFeedback('');
        if (publishers.length === 0) {
            setError('Selecciona al menos una cuenta');
            return;
        }
        if (listChannels.length === 0) {
            setError('Agrega al menos un canal a la lista');
            return;
        }
        setSaving(true);
        try {
            await AccountsUsersService.setPercentPublishers({
                publishers: publishers.map((p) => p.value),
                channels: listChannels,
            });
            setFeedback('Porcentajes guardados correctamente');
            setListChannels([]);
            setPublishers([]);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSearch = async () => {
        setError('');
        setFeedback('');
        if (!searchPub) {
            setError('Selecciona una cuenta para buscar');
            return;
        }
        setLoading(true);
        try {
            const data = await AccountsUsersService.getPercentPublishers({
                id_publisher: searchPub ? searchPub.value : 0,
                id_channel: searchChannel !== '0' ? searchChannel : 0,
                business_model: searchModel !== 'all' ? searchModel : '',
                content_type: searchType !== 'all' ? searchType : '',
                apply: searchApply !== 'all' ? searchApply : '',
            });
            setResults(data);
            setSelectedIds([]);
        } catch (e) {
            setError(e.message);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (row) => setDeleteTarget({ mode: 'single', row });

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        setDeleteTarget({ mode: 'bulk', ids: selectedIds });
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        setError('');
        try {
            if (deleteTarget.mode === 'bulk') {
                for (const id of deleteTarget.ids) {
                    await AccountsUsersService.deletePercentPublisher(id);
                }
            } else {
                await AccountsUsersService.deletePercentPublisher(deleteTarget.row.id);
            }
            setDeleteTarget(null);
            await handleSearch();
        } catch (e) {
            setError(e.message);
        } finally {
            setDeleting(false);
        }
    };

    const toggleSelected = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const allSelected = results && results.length > 0 && results.every((r) => selectedIds.includes(r.id));

    const toggleSelectAll = () => {
        if (!results) return;
        setSelectedIds(allSelected ? [] : results.map((r) => r.id));
    };

    const modelOptions = [{ value: 'all', label: 'Todos' }, ...BUSINESS_MODELS];
    const typeOptions = [{ value: 'all', label: 'Todos' }, ...CONTENT_TYPES];
    const applyOptions = [{ value: 'all', label: 'Todos' }, ...APPLY_OPTIONS];

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <div className="p-4">
                <h4 className="text-white mb-1">Porcentajes Ink It / Cuentas</h4>
                <div className="text-white-50 small mb-3">
                    Tienda, % Ink, % Editorial, modelo de negocio y tipo de contenido por cuenta.
                </div>

                <div className="d-flex gap-2 mb-3">
                    <button type="button" className={`btn btn-sm px-3 ${tab === 'add' ? 'btn-primary' : 'btn-outline-secondary border-secondary text-white-50'}`} onClick={() => setTab('add')}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" /> Agregar
                    </button>
                    <button type="button" className={`btn btn-sm px-3 ${tab === 'search' ? 'btn-primary' : 'btn-outline-secondary border-secondary text-white-50'}`} onClick={() => setTab('search')}>
                        <FontAwesomeIcon icon={faSearch} className="me-1" /> Buscar
                    </button>
                </div>

                {error && (
                    <div className="alert alert-danger py-2">{error}
                        <button className="btn-close ms-2 float-end" onClick={() => setError('')} />
                    </div>
                )}
                {feedback && (
                    <div className="alert alert-success py-2">{feedback}
                        <button className="btn-close ms-2 float-end" onClick={() => setFeedback('')} />
                    </div>
                )}

                {tab === 'add' && (
                    <div>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Tienda</label>
                                <select className="form-select bg-dark text-white border-secondary py-2" value={idChannel} onChange={(e) => setIdChannel(e.target.value)}>
                                    <option value="0">Todas</option>
                                    {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Tipo de contenido</label>
                                <select className="form-select bg-dark text-white border-secondary py-2" value={contentType} onChange={(e) => setContentType(e.target.value)}>
                                    {CONTENT_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Modelo de negocio</label>
                                <select className="form-select bg-dark text-white border-secondary py-2" value={businessModel} onChange={(e) => setBusinessModel(e.target.value)}>
                                    {BUSINESS_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">% Ink</label>
                                <input type="number" step="any" className="form-control bg-dark text-white border-secondary py-2" value={percentInk} onChange={(e) => setPercentInk(e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">% Editorial</label>
                                <input type="number" step="any" className="form-control bg-dark text-white border-secondary py-2" value={percentPublisher} onChange={(e) => setPercentPublisher(e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Aplicado al</label>
                                <select className="form-select bg-dark text-white border-secondary py-2" value={apply} onChange={(e) => setApply(e.target.value)}>
                                    {APPLY_OPTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end mt-3">
                            <button type="button" className="btn btn-primary px-3" onClick={addToList}>
                                <FontAwesomeIcon icon={faPlus} className="me-1" /> Agregar a la lista
                            </button>
                        </div>

                        <div className="text-white-50 small fw-bold text-uppercase mt-4 mb-2" style={{ letterSpacing: '0.05em' }}>Lista de % a insertar</div>
                        {listChannels.length === 0 ? (
                            <div className="text-white-50 small">Sin canales agregados.</div>
                        ) : (
                            <div className="modern-table-container">
                                <table className="modern-table striped-table">
                                    <thead>
                                        <tr>
                                            <th>Tienda</th>
                                            <th>% Ink</th>
                                            <th>% Editorial</th>
                                            <th>Aplicado a</th>
                                            <th>Modelo</th>
                                            <th>Contenido</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {listChannels.map((l, i) => (
                                            <tr key={i}>
                                                <td className="short text-white">{l.channel_name}</td>
                                                <td className="short text-white-50">{l.percent_ink}</td>
                                                <td className="short text-white-50">{l.percent_publisher}</td>
                                                <td className="short text-white-50">{APPLY_OPTIONS.find((a) => a.value === l.apply)?.label}</td>
                                                <td className="short text-white-50">{BUSINESS_MODELS.find((m) => m.value === l.business_model)?.label}</td>
                                                <td className="short text-white-50">{CONTENT_TYPES.find((c) => c.value === l.content_type)?.label}</td>
                                                <td>
                                                    <button type="button" className="btn btn-sm btn-link text-danger p-0" title="Quitar de la lista" onClick={() => removeFromList(i)}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="mt-4 mb-2">
                            <label className="form-label text-white-50">Cuentas *</label>
                            <PublisherSelect
                                isMulti
                                value={publishers}
                                onChange={setPublishers}
                                placeholder="Busca y selecciona cuentas..."
                            />
                        </div>

                        <div className="d-flex justify-content-end gap-3 mt-4">
                            <button className="btn btn-outline-secondary px-4 rounded-pill" onClick={onClose} disabled={saving}>Cancelar</button>
                            <button className="btn btn-success px-4 rounded-pill" onClick={handleSave} disabled={saving}>
                                {saving && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                                <FontAwesomeIcon icon={faSave} className="me-1" /> Guardar
                            </button>
                        </div>
                    </div>
                )}

                {tab === 'search' && (
                    <div>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Cuenta *</label>
                                <PublisherSelect
                                    value={searchPub}
                                    onChange={(opt) => { setSearchPub(opt); setSelectedIds([]); }}
                                    placeholder="Selecciona una cuenta..."
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Tienda</label>
                                <select className="form-select bg-dark text-white border-secondary py-2" value={searchChannel} onChange={(e) => setSearchChannel(e.target.value)}>
                                    <option value="0">Todas</option>
                                    {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Modelo de negocio</label>
                                <select className="form-select bg-dark text-white border-secondary py-2" value={searchModel} onChange={(e) => setSearchModel(e.target.value)}>
                                    {modelOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Tipo de contenido</label>
                                <select className="form-select bg-dark text-white border-secondary py-2" value={searchType} onChange={(e) => setSearchType(e.target.value)}>
                                    {typeOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label text-white-50">Aplicado al</label>
                                <select className="form-select bg-dark text-white border-secondary py-2" value={searchApply} onChange={(e) => setSearchApply(e.target.value)}>
                                    {applyOptions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                                </select>
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                                <button className="btn btn-ink-search w-100" onClick={handleSearch}>
                                    <FontAwesomeIcon icon={faSearch} className="me-1" /> Buscar
                                </button>
                            </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                            <div className="text-white-50 small fw-bold text-uppercase" style={{ letterSpacing: '0.05em' }}>Datos</div>
                            {selectedIds.length > 0 && (
                                <button type="button" className="btn btn-sm btn-outline-danger border-danger text-danger px-3" onClick={handleBulkDelete}>
                                    <FontAwesomeIcon icon={faTrash} className="me-1" /> Eliminar seleccionados ({selectedIds.length})
                                </button>
                            )}
                        </div>
                        {loading ? <div className="text-center py-4 text-white-50"><FontAwesomeIcon icon={faSpinner} spin /></div> : (
                            results === null ? (
                                <div className="text-white-50 small">Selecciona una cuenta y usa el buscador para listar sus porcentajes.</div>
                            ) : results.length === 0 ? (
                                <div className="text-white-50 small">No hay datos con el criterio de búsqueda.</div>
                            ) : (
                                <div className="modern-table-container">
                                    <table className="modern-table striped-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 40 }}>
                                                    <input type="checkbox" className="form-check-input" checked={allSelected} onChange={toggleSelectAll} title="Seleccionar todo" />
                                                </th>
                                                <th>Tienda</th>
                                                <th>% Ink</th>
                                                <th>% Editorial</th>
                                                <th>Aplicado a</th>
                                                <th>Modelo</th>
                                                <th>Contenido</th>
                                                <th>Fecha</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {results.map((r) => (
                                                <tr key={r.id}>
                                                    <td>
                                                        <input type="checkbox" className="form-check-input" checked={selectedIds.includes(r.id)} onChange={() => toggleSelected(r.id)} />
                                                    </td>
                                                    <td className="short text-white">{r.channel_name}</td>
                                                    <td className="short text-white-50">{r.percent_ink}</td>
                                                    <td className="short text-white-50">{r.percent_publisher}</td>
                                                    <td className="short text-white-50">{r.apply}</td>
                                                    <td className="short text-white-50">{r.business_model}</td>
                                                    <td className="short text-white-50">{r.content_type}</td>
                                                    <td className="short text-white-50">{r.date}</td>
                                                    <td>
                                                        <button type="button" className="btn btn-sm btn-link text-danger p-0" title="Eliminar" onClick={() => handleDelete(r)}>
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>

            <ConfirmationModal
                isOpen={deleteTarget !== null}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title="Eliminar porcentaje"
                message={
                    deleteTarget && deleteTarget.mode === 'bulk'
                        ? `¿Estás seguro de eliminar ${deleteTarget.ids.length} registro(s) seleccionado(s)?`
                        : `¿Estás seguro de eliminar el registro de ${deleteTarget && deleteTarget.row ? deleteTarget.row.channel_name : ''}?`
                }
                confirmText="Eliminar"
                type="danger"
                isLoading={deleting}
            />
        </Modal>
    );
}

export default PercentPublishersModal;